import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ChefHat, Flame, Salad, Pizza, Coffee, Check, Clock, 
  Volume2, VolumeX, ArrowLeft, RefreshCw, AlertCircle,
  Timer, CheckCircle2, Package
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Station icons mapping
const STATION_ICONS = {
  kueche: ChefHat,
  fritteuse: Flame,
  salat: Salad,
  pizza: Pizza,
  getraenke: Coffee,
  grill: Flame
};

// Station colors
const STATION_COLORS = {
  kueche: 'from-red-500 to-rose-600',
  fritteuse: 'from-orange-500 to-amber-600',
  salat: 'from-green-500 to-emerald-600',
  pizza: 'from-rose-500 to-pink-600',
  getraenke: 'from-purple-500 to-violet-600',
  grill: 'from-red-600 to-orange-600'
};

// Station names
const STATION_NAMES = {
  kueche: 'Küche',
  fritteuse: 'Fritteuse',
  salat: 'Salatstation',
  pizza: 'Pizza-Station',
  getraenke: 'Getränke',
  grill: 'Grill'
};

// Notification Sound
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkJOPi4J3aF1YXWh0gYuTl5GPh3xxZl1aX2h1g46Wm5iSiH51aWFdYWp3hZCZnpqUi4F4bmVgYGdyfYqUnp6ak4uCeXBnY2Vpdn+MlZ6fnJWNhHx0bGhlanuGj5ifn5yWj4aBenNsaWt3goyWn5+cl5GJgnt1b2xud4OOl5+fnpiSioN9d3JvcnmFj5ifn52Zk4yFgHp2c3V8h5GZn5+dmJONh4J9eXZ4fomSmZ6enJmUjoiDf3x5e4CLlJqenp2alJCKhYF+fH2DjZabnp6dmZWRi4aEgX9/hpCYnJ6enJqWkYyIhYOBhIqTmZyen52bmJOPi4iGhYaLlJqcnp6dnJiUkI2KiIaIjZabnZ6enZyZlZGOi4mIi5CYnJ6enp2cmJWSkI2LioqPlpudnp6enZqXlJGPjYuMkZebnZ6enp2blpSTkI6NjZKYnJ2enp6dm5iVk5GQjo+Um5ydnp6enZuYlpSSkJCRl5ycnZ6enp2bmJaUk5GRk5mbnZ2enp6dm5mXlZOSkpSam5ydnp6enZuZl5aUk5OVm5ydnZ6enp2bmZeWlJSUl5ucnZ2enp6dnJqYl5aVlZebnJ2dnp6enZ2bmpiXlpaYm5ydnZ6enp6dnJqZmJeXmZucnZ2enp6enZybmpmYmJqbnJ2dnp6enp2cm5qZmZmbnJydnZ6enp6dnJuampucnJydnZ6enp6enZyb';

export default function KitchenStation({ 
  stationType, 
  sessionToken, 
  businessName,
  onBack 
}) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const previousOrderCountRef = useRef(0);
  const audioRef = useRef(null);

  const IconComponent = STATION_ICONS[stationType] || ChefHat;
  const stationColor = STATION_COLORS[stationType] || 'from-gray-500 to-gray-600';
  const stationName = STATION_NAMES[stationType] || 'Station';

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.7;
  }, []);

  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Fetch orders for this station
  const fetchOrders = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/kitchen/orders?station_type=${stationType}`,
        { headers: { 'X-Session-Token': sessionToken } }
      );
      
      if (response.ok) {
        const data = await response.json();
        const newOrderCount = data.orders?.length || 0;
        
        // Play sound on new orders
        if (newOrderCount > previousOrderCountRef.current && previousOrderCountRef.current > 0) {
          playNotificationSound();
          toast.success('🔔 Neue Bestellung!', { duration: 5000 });
        }
        previousOrderCountRef.current = newOrderCount;
        
        setOrders(data.orders || []);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Fetch orders error:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, stationType, playNotificationSound]);

  // Poll for orders
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000); // Every 5 seconds
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Mark order as ready for this station
  const markOrderReady = async (orderId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/kitchen/order-ready?order_id=${orderId}&station_type=${stationType}`,
        {
          method: 'POST',
          headers: { 'X-Session-Token': sessionToken }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.all_ready ? '✅ Bestellung komplett fertig!' : '✅ Station fertig!');
        fetchOrders();
      } else {
        toast.error('Fehler beim Markieren');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Mark single item as ready
  const markItemReady = async (orderId, itemIndex) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/stations/kitchen/item-ready?order_id=${orderId}&item_index=${itemIndex}&station_type=${stationType}`,
        {
          method: 'POST',
          headers: { 'X-Session-Token': sessionToken }
        }
      );
      
      if (response.ok) {
        toast.success('Artikel fertig!');
        fetchOrders();
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Calculate time since order
  const getTimeSince = (timestamp) => {
    if (!timestamp) return '';
    const diff = Math.floor((new Date() - new Date(timestamp)) / 1000 / 60);
    if (diff < 1) return 'Gerade eben';
    if (diff < 60) return `${diff} Min.`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
  };

  // Get urgency color based on time
  const getUrgencyColor = (timestamp) => {
    if (!timestamp) return 'text-gray-400';
    const diff = Math.floor((new Date() - new Date(timestamp)) / 1000 / 60);
    if (diff < 5) return 'text-green-400';
    if (diff < 10) return 'text-amber-400';
    if (diff < 15) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className={`bg-gradient-to-r ${stationColor} p-4 shadow-lg`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={onBack}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <IconComponent className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{stationName}</h1>
                <p className="text-white/70 text-sm">{businessName}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Order Count */}
            <div className="bg-white/20 px-4 py-2 rounded-lg">
              <span className="text-white font-bold text-xl">{orders.length}</span>
              <span className="text-white/70 ml-2">Bestellungen</span>
            </div>
            
            {/* Sound Toggle */}
            <Button
              variant="ghost"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-white hover:bg-white/20"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            
            {/* Refresh */}
            <Button
              variant="ghost"
              onClick={fetchOrders}
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="p-4 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-400">Lade Bestellungen...</div>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <Package className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-xl">Keine Bestellungen für diese Station</p>
            <p className="text-sm mt-2">Neue Bestellungen erscheinen hier automatisch</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {orders.map((order) => (
              <Card 
                key={order.order_id}
                data-testid={`kitchen-order-${order.order_id}`}
                className="bg-gray-800/80 border-gray-700 overflow-hidden"
              >
                {/* Order Header */}
                <CardHeader className="pb-2 bg-gray-700/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg text-white">
                        #{order.order_number}
                      </CardTitle>
                      <div className={`flex items-center gap-1 text-sm ${getUrgencyColor(order.created_at)}`}>
                        <Timer className="w-4 h-4" />
                        {getTimeSince(order.created_at)}
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`
                        ${order.status === 'new' ? 'bg-amber-500/20 text-amber-400 border-amber-500' : ''}
                        ${order.status === 'processing' ? 'bg-blue-500/20 text-blue-400 border-blue-500' : ''}
                      `}
                    >
                      {order.status === 'new' ? 'Neu' : 'In Arbeit'}
                    </Badge>
                  </div>
                </CardHeader>
                
                {/* Station Items */}
                <CardContent className="pt-3">
                  <ScrollArea className="h-48">
                    <div className="space-y-2">
                      {order.station_items?.map((item, index) => (
                        <div 
                          key={index}
                          className={`
                            flex items-center justify-between p-2 rounded-lg
                            ${item.ready ? 'bg-green-500/20 border border-green-500/30' : 'bg-gray-700/50'}
                            transition-all duration-300
                          `}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-amber-400 font-bold">
                              {item.quantity}x
                            </span>
                            <span className={item.ready ? 'text-green-400 line-through' : 'text-white'}>
                              {item.name}
                            </span>
                          </div>
                          
                          {!item.ready && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => markItemReady(order.order_id, index)}
                              className="text-green-400 hover:bg-green-500/20 h-8 w-8 p-0"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {item.ready && (
                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  
                  {/* Item Notes */}
                  {order.notes && (
                    <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-amber-400 text-sm">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        {order.notes}
                      </p>
                    </div>
                  )}
                  
                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Fortschritt</span>
                      <span>
                        {order.station_items?.filter(i => i.ready).length || 0} / {order.station_item_count}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
                        style={{ 
                          width: `${((order.station_items?.filter(i => i.ready).length || 0) / (order.station_item_count || 1)) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Complete Button */}
                  <Button
                    className={`w-full mt-4 bg-gradient-to-r ${stationColor} text-white font-bold`}
                    onClick={() => markOrderReady(order.order_id)}
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Station fertig
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer with last update */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800/80 backdrop-blur-sm p-2 text-center text-gray-400 text-sm">
        Letzte Aktualisierung: {lastUpdate?.toLocaleTimeString('de-DE') || '-'}
        <span className="ml-4">Auto-Refresh alle 5 Sekunden</span>
      </div>
    </div>
  );
}
